import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { CheckCircle, Envelope, ChatCircleDots, X, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * PostIssueDeliveryDialog — opens after a card has been successfully
 * issued (either cash-funded immediately, or Stripe-funded after the
 * Checkout reconcile succeeds). Shows the card image plus two delivery
 * actions: send by email and send by text/MMS.
 *
 * The user can edit the destination email / phone before sending in case
 * the receiver typo'd at issuance. Each channel tracks its own busy /
 * sent / error state independently so a failed SMS doesn't block the
 * agent from re-trying email.
 *
 * Props:
 *   - cardId: the active card to deliver. When null/undefined the dialog
 *     is closed.
 *   - onClose: called when the user dismisses the dialog (either via the
 *     Done button or backdrop click).
 */
export default function PostIssueDeliveryDialog({ cardId, onClose }) {
  const t = useT();
  const [card, setCard] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [emailTo, setEmailTo] = useState("");
  const [phoneTo, setPhoneTo] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [smsSent, setSmsSent] = useState(false);

  const open = !!cardId;

  useEffect(() => {
    if (!cardId) return undefined;
    let cancelled = false;
    setLoading(true);
    setCard(null);
    setShareUrl("");
    setEmailSent(false);
    setSmsSent(false);
    (async () => {
      try {
        const [cR, sR] = await Promise.all([
          api.get(`/cards/${cardId}`),
          api.post(`/cards/${cardId}/image-share-url`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setCard(cR.data);
        setEmailTo(cR.data.receiver_email || "");
        setPhoneTo(cR.data.receiver_phone || "");
        setShareUrl(sR?.data?.url || "");
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Could not load card");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cardId]);

  const sendEmail = async () => {
    const dest = emailTo.trim();
    if (!dest) {
      toast.error("Enter a receiver email");
      return;
    }
    setEmailBusy(true);
    try {
      await api.post(`/cards/${cardId}/email`, { to_email: dest });
      setEmailSent(true);
      toast.success(`Card emailed to ${dest}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email delivery failed");
    } finally {
      setEmailBusy(false);
    }
  };

  const sendText = async () => {
    const dest = phoneTo.trim();
    if (!dest) {
      toast.error("Enter a receiver phone number");
      return;
    }
    setSmsBusy(true);
    try {
      await api.post(`/cards/${cardId}/text`, { to_phone: dest });
      setSmsSent(true);
      toast.success(`Card texted to ${dest}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Text delivery failed");
    } finally {
      setSmsBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="bg-[#FBF9F6] border-[#E5E0D8] max-w-xl"
        data-testid="post-issue-delivery-dialog"
      >
        <DialogHeader>
          <DialogTitle className="serif text-3xl text-[#184A30] flex items-center gap-2">
            <CheckCircle size={28} weight="fill" className="text-emerald-600" />
            Card is ready
          </DialogTitle>
          <DialogDescription className="text-[#4A5C50]">
            Funds received. Choose how you&apos;d like to deliver the card to the receiver.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-[#4A5C50] flex items-center justify-center gap-2">
            <Spinner size={20} className="animate-spin" /> Loading card…
          </div>
        ) : !card ? (
          <div className="py-10 text-center text-rose-700" data-testid="post-issue-card-error">
            Could not load card details.
          </div>
        ) : (
          <div className="space-y-5">
            {/* Card image preview */}
            <div className="rounded-2xl bg-stone-100 p-3 flex items-center justify-center" data-testid="post-issue-card-preview-wrap">
              {shareUrl ? (
                <img
                  src={shareUrl}
                  alt="Card preview"
                  className="rounded-xl shadow-md max-h-[200px] w-auto"
                  data-testid="post-issue-card-preview-img"
                />
              ) : (
                <div className="rounded-xl bg-stone-200 h-[150px] w-full flex items-center justify-center text-[#4A5C50] text-xs">
                  Image preview unavailable
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-[#E5E0D8] p-3 text-xs text-[#4A5C50]" data-testid="post-issue-card-meta">
              <div className="flex items-center justify-between">
                <span>Receiver</span>
                <span className="text-[#112318] font-semibold">{card.receiver_name || "—"}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span>Card balance</span>
                <span className="mono text-[#184A30] font-semibold">${Number(card.balance ?? 0).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span>Last 4</span>
                <span className="mono text-[#112318]">•••• {card.last4 || "—"}</span>
              </div>
            </div>

            {/* Send by EMAIL */}
            <div className="rounded-xl border border-[#E5E0D8] bg-white p-4 space-y-2" data-testid="post-issue-email-block">
              <Label className="text-xs uppercase tracking-widest text-[#4A5C50] flex items-center gap-1.5">
                <Envelope size={13} weight="duotone" /> Send by email
              </Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={emailTo}
                  onChange={(e) => { setEmailTo(e.target.value); setEmailSent(false); }}
                  placeholder="receiver@email.com"
                  className="flex-1"
                  data-testid="post-issue-email-input"
                  disabled={emailBusy}
                />
                <Button
                  type="button"
                  onClick={sendEmail}
                  disabled={emailBusy || !emailTo.trim()}
                  className={emailSent ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#184A30] hover:bg-[#0F3320] text-white"}
                  data-testid="post-issue-email-send-btn"
                >
                  {emailBusy ? <Spinner size={14} className="animate-spin" /> : emailSent ? <><CheckCircle size={14} weight="fill" className="mr-1" /> Sent</> : "Send email"}
                </Button>
              </div>
              {emailSent && (
                <p className="text-[11px] text-emerald-700" data-testid="post-issue-email-sent-confirm">
                  Email delivered. You can resend by editing the address or clicking again.
                </p>
              )}
            </div>

            {/* Send by SMS / MMS */}
            <div className="rounded-xl border border-[#E5E0D8] bg-white p-4 space-y-2" data-testid="post-issue-sms-block">
              <Label className="text-xs uppercase tracking-widest text-[#4A5C50] flex items-center gap-1.5">
                <ChatCircleDots size={13} weight="duotone" /> Send by text message
              </Label>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  value={phoneTo}
                  onChange={(e) => { setPhoneTo(e.target.value); setSmsSent(false); }}
                  placeholder="+1 415 555 0123"
                  className="flex-1"
                  data-testid="post-issue-sms-input"
                  disabled={smsBusy}
                />
                <Button
                  type="button"
                  onClick={sendText}
                  disabled={smsBusy || !phoneTo.trim()}
                  className={smsSent ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#184A30] hover:bg-[#0F3320] text-white"}
                  data-testid="post-issue-sms-send-btn"
                >
                  {smsBusy ? <Spinner size={14} className="animate-spin" /> : smsSent ? <><CheckCircle size={14} weight="fill" className="mr-1" /> Sent</> : "Send text"}
                </Button>
              </div>
              {smsSent && (
                <p className="text-[11px] text-emerald-700" data-testid="post-issue-sms-sent-confirm">
                  Text dispatched. May take up to a minute to reach the receiver.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onClose?.()}
            data-testid="post-issue-close-btn"
          >
            <X size={14} className="mr-1" /> Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
